/**
 * TelephoneService
 * 
 * Centralized service for handling telephone number operations including:
 * - Initiating phone calls via tel: protocol
 * - Phone number formatting and cleaning
 * - Future: Phone system integration (VoIP, SIP, etc.)
 * - Future: Country code prefixing based on system configuration
 * 
 * @example
 * // Make a call
 * TelephoneService.call('+44 1234 567890');
 * 
 * // Clean a phone number
 * const clean = TelephoneService.cleanNumber('(01onal234) 567-890');
 * // Returns: '01234567890'
 * 
 * // Format for display
 * const formatted = TelephoneService.formatForDisplay('01onal234567890', 'GB');
 * // Returns: '+44 1234 567890'
 */

import { Logger } from '@/services/Logger';
import { ToasterService } from '@/services/ToasterService';

const log = Logger.scope('TelephoneService');

/**
 * Configuration options for the telephone service
 */
export interface TelephoneConfig {
  /** Default country code to use when none is provided (e.g., 'GB', 'US') */
  defaultCountryCode?: string;
  
  /** Default dialing prefix for the country (e.g., '+44' for UK) */
  defaultDialingPrefix?: string;
  
  /** Phone system type for enterprise integration */
  phoneSystemType?: 'tel' | 'sip' | 'callto' | 'custom';
  
  /** Custom URL pattern for phone system integration (use {number} placeholder) */
  customUrlPattern?: string;
  
  /** Whether to strip leading zero when adding country prefix */
  stripLeadingZeroForInternational?: boolean;
}

/**
 * Default configuration
 */
const defaultConfig: TelephoneConfig = {
  defaultCountryCode: 'GB',
  defaultDialingPrefix: '+44',
  phoneSystemType: 'tel',
  stripLeadingZeroForInternational: true
};

/**
 * Current active configuration
 */
let currentConfig: TelephoneConfig = { ...defaultConfig };

/**
 * TelephoneService - Centralized telephone number handling
 */
export const TelephoneService = {
  /**
   * Configure the telephone service
   * @param config - Configuration options to merge with defaults
   */
  configure(config: Partial<TelephoneConfig>): void {
    currentConfig = { ...defaultConfig, ...config };
    log.debug('TelephoneService configured', { config: currentConfig });
  },
  
  /**
   * Get current configuration
   */
  getConfig(): TelephoneConfig {
    return { ...currentConfig };
  },
  
  /**
   * Reset to default configuration
   */
  resetConfig(): void {
    currentConfig = { ...defaultConfig };
  },
  
  /**
   * Clean a phone number by removing formatting characters
   * Keeps only digits and the leading + for international format
   * 
   * @param phoneNumber - Raw phone number with possible formatting
   * @returns Cleaned phone number suitable for dialing
   */
  cleanNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    
    // Trim whitespace
    let cleaned = phoneNumber.trim();
    
    // Keep + only at the start, remove all other non-digits
    if (cleaned.startsWith('+')) {
      cleaned = '+' + cleaned.slice(1).replace(/\D/g, '');
    } else {
      cleaned = cleaned.replace(/\D/g, '');
    }
    
    return cleaned;
  },
  
  /**
   * Format a phone number for international dialing
   * Adds country prefix if not present and configured
   * 
   * @param phoneNumber - Phone number to format
   * @param addCountryPrefix - Whether to add country prefix if missing (default: true)
   * @returns Phone number formatted for dialing
   */
  formatForDialing(phoneNumber: string, addCountryPrefix: boolean = true): string {
    let cleaned = this.cleanNumber(phoneNumber);
    
    if (!cleaned) return '';
    
    // Already has international prefix
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // Add country prefix if configured and requested
    if (addCountryPrefix && currentConfig.defaultDialingPrefix) {
      // Strip leading zero if configured (e.g., 01onal234 -> 1234 for UK)
      if (currentConfig.stripLeadingZeroForInternational && cleaned.startsWith('0')) {
        cleaned = cleaned.slice(1);
      }
      cleaned = currentConfig.defaultDialingPrefix + cleaned;
    }
    
    return cleaned;
  },
  
  /**
   * Build the URL for initiating a phone call
   * Supports different phone system types (tel:, sip:, callto:, custom)
   * 
   * @param phoneNumber - Phone number to call
   * @returns URL string for initiating the call
   */
  buildCallUrl(phoneNumber: string): string {
    const dialNumber = this.formatForDialing(phoneNumber);
    
    if (!dialNumber) return '';
    
    switch (currentConfig.phoneSystemType) {
      case 'sip':
        return `sip:${dialNumber}`;
      
      case 'callto':
        // Skype/legacy format
        return `callto:${dialNumber}`;
      
      case 'custom':
        // Custom URL pattern with {number} placeholder
        if (currentConfig.customUrlPattern) {
          return currentConfig.customUrlPattern.replace('{number}', encodeURIComponent(dialNumber));
        }
        // Fall back to tel: if no custom pattern
        return `tel:${dialNumber}`;
      
      case 'tel':
      default:
        return `tel:${dialNumber}`;
    }
  },
  
  /**
   * Copy phone number to clipboard and show toast notification
   * 
   * @param phoneNumber - Phone number to copy
   * @returns true if copy was initiated, false if phone number was empty/invalid
   */
  async call(phoneNumber: string): Promise<boolean> {
    const dialNumber = this.formatForDialing(phoneNumber);
    
    if (!dialNumber) {
      log.warn('Cannot copy: empty or invalid phone number', { phoneNumber });
      return false;
    }
    
    log.debug('Copying phone number to clipboard', { 
      original: phoneNumber, 
      dialNumber 
    });
    
    try {
      await navigator.clipboard.writeText(dialNumber);
      ToasterService.info(`Phone number copied: ${dialNumber}`);
      log.debug('Phone number copied to clipboard', { dialNumber });
      return true;
    } catch (error) {
      log.error('Failed to copy phone number to clipboard', { error });
      ToasterService.error('Failed to copy phone number');
      return false;
    }
  },
  
  /**
   * Format a phone number for display (human-readable)
   * This is a basic implementation - can be enhanced with libphonenumber for proper formatting
   * 
   * @param phoneNumber - Phone number to format
   * @returns Formatted phone number for display
   */
  formatForDisplay(phoneNumber: string): string {
    const cleaned = this.cleanNumber(phoneNumber);
    
    if (!cleaned) return '';
    
    // Basic formatting for UK numbers
    // This could be enhanced with a proper library like libphonenumber-js
    if (cleaned.startsWith('+44')) {
      const national = cleaned.slice(3);
      if (national.length === 10) {
        // Mobile: +44 7XXX XXXXXX
        if (national.startsWith('7')) {
          return `+44 ${national.slice(0, 4)} ${national.slice(4)}`;
        }
        // Landline: +44 1XXX XXXXXX or +44 2X XXXX XXXX
        return `+44 ${national.slice(0, 4)} ${national.slice(4)}`;
      }
    }
    
    // For other formats, just return cleaned number
    return cleaned;
  },
  
  /**
   * Check if a phone number appears to be valid
   * Basic validation - can be enhanced with proper validation library
   * 
   * @param phoneNumber - Phone number to validate
   * @returns true if appears valid, false otherwise
   */
  isValid(phoneNumber: string): boolean {
    const cleaned = this.cleanNumber(phoneNumber);
    
    if (!cleaned) return false;
    
    // At minimum, should have some digits
    const digitsOnly = cleaned.replace(/\D/g, '');
    
    // Most phone numbers are at least 7 digits (local) to 15 digits (international max)
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  }
};

export default TelephoneService;
